import React, { useEffect } from 'react';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import queryString from 'query-string';
import { IProps } from './types';
import { RequestStatus } from '../../../../shared/enums/request-status';
import toaster, { Theme } from '../../../../shared/toaster';
import store from '../../../../store';
import { OAuthMethod } from '../oauth-buttons';
import {
  initializeThirdPartyIntegrations,
} from '../../../../shared/utils/initialize-third-party-integrations';
import { pushSignupEvent } from '../../../../shared/utils/gtm-events';
import {
  AuthHelper,
  generateSerpLeadRedirectUrl,
} from '../../../../shared/utils/auth-helper';
import {
  executeOnErrorWithErrorCheck,
  executeOnRequestStatus,
  getCurrentTimeZone,
  redirectWithoutRefresh,
  setToken,
} from '../../../../shared/utils';
import {
  ActionForAuthentication,
  GoogleOneTapStashedPayload,
} from '../../types';
import { GOOGLE_ONE_TAP_SESSION_KEY } from '../oauth-buttons/constants';
import { AuthCredentialType } from '../../enums/auth-credential-type';

const OAuthAuthenticateCallback: React.FC<IProps> = (props) => {
  const {
    match,
    location,
    history,
    status,
    message,
    error,
    sendOAuthAuthenticateRequest,
    token,
    trackingId,
    firstName,
    lastName,
    email,
    action,
    agencyConfig,
    redirectLeadFinderUrl,
    utmSource,
    sendGetAgencyConfigRequest,
    getAgencyConfigRequestStatus,
    sendDeleteMyProfileRequest,
    deleteMyProfileRequestStatus,
    deleteMyProfileRequestError,
    deleteMyProfileRequestMessage,
    resetDeleteMyProfileRequest,
  } = props;

  const redirectToHome = () => {
    history.push('/');
  };

  const redirectToLogin = () => {
    history.push('/login');
  };

  // Handle initial authentication
  useEffect(() => {
    const { method } = match.params;
    const query = location.search;
    location.search = '';

    const {
      code,
      state,
      error: queryError,
      source,
      credential: credentialFromUrl,
      action: actionFromUrl,
      utmSource: utmSourceFromUrl,
    } = queryString.parse(query);

    let oneTapPayload: GoogleOneTapStashedPayload | null = null;
    if (source === 'one-tap' && !code) {
      if (credentialFromUrl) {
        oneTapPayload = {
          credential: credentialFromUrl.toString(),
          action: actionFromUrl?.toString() as
            | ActionForAuthentication
            | undefined,
          utmSource: utmSourceFromUrl?.toString(),
        };
      } else {
        try {
          const raw = window.sessionStorage.getItem(GOOGLE_ONE_TAP_SESSION_KEY);
          if (raw) {
            oneTapPayload = JSON.parse(raw);
          }
        } catch {
          oneTapPayload = null;
        }
      }
    }
    try {
      window.sessionStorage.removeItem(GOOGLE_ONE_TAP_SESSION_KEY);
    } catch {
      /* no-op */
    }

    if (queryError) {
      toaster.error('Authentication failed', {
        theme: Theme.New,
        showCloseIcon: true,
      });
      redirectToLogin();
    } else if (code) {
      // OAuth-redirect flow.
      const oauthMethod = method as OAuthMethod;
      sendOAuthAuthenticateRequest(
        oauthMethod,
        code.toString(),
        state?.toString(),
      );
      store.dispatch(showLoading());
    } else if (oneTapPayload?.credential) {
      // Google One Tap flow.
      const oauthMethod = method as OAuthMethod;
      sendOAuthAuthenticateRequest(
        oauthMethod,
        oneTapPayload.credential,
        undefined,
        {
          credentialType: AuthCredentialType.GoogleIdToken,
          action: oneTapPayload.action,
          invitedEmail: oneTapPayload.invitedEmail,
          utmSource: oneTapPayload.utmSource,
          redirectLeadFinderUrl: oneTapPayload.redirectLeadFinderUrl,
          rewardfulReferralId: oneTapPayload.rewardfulReferralId,
          celloReferralCode: oneTapPayload.celloReferralCode,
          timeZone: getCurrentTimeZone(),
        },
      );
      store.dispatch(showLoading());
    } else {
      // No code, no One Tap credential, no error in the URL.
      redirectToLogin();
    }
  }, []);

  // Handle authentication success and fetch agency config if needed
  useEffect(() => {
    if (status === RequestStatus.Succeeded || status === RequestStatus.Failed) {
      const clientBaseDomain = window.sessionStorage.getItem(
        'oauth-whitelabel-domain',
      );
      store.dispatch(hideLoading());

      if (status === RequestStatus.Succeeded && token && email) {
        const agencyConfigPayload = {
          baseUrl: window.location.origin,
          email,
        };
        sendGetAgencyConfigRequest(agencyConfigPayload);
      }

      if (
        status === RequestStatus.Succeeded &&
        !token &&
        !email &&
        message === 'Logged in successfully'
      ) {
        if (clientBaseDomain) {
          window.location.href = `http://${clientBaseDomain}/oauth/whitelabel/callback?message=email-changed-successfully`;
          return;
        }
        if (AuthHelper.isAuthenticated) {
          toaster.success('Email changed successfully', { theme: Theme.New });
          redirectToHome();
        }
        redirectToLogin();
      }

      if (status === RequestStatus.Failed) {
        if (clientBaseDomain) {
          window.location.href = `http://${clientBaseDomain}/oauth/whitelabel/callback?message=authentication-failed`;
          return;
        }
        if (error) {
          const errorMessage = error?.message || 'Authentication failed.';
          toaster.error(errorMessage, {
            theme: Theme.New,
            showCloseIcon: true,
            delay: 10000,
          });
        }
        redirectToLogin();
      }
    }
  }, [status, token, email]);

  // Handle post-authentication actions based on agency config
  useEffect(() => {
    if (
      getAgencyConfigRequestStatus === RequestStatus.Succeeded &&
      agencyConfig &&
      token
    ) {
      // Handle agency redirect if applicable
      if (agencyConfig?.clientBaseUrl) {
        // Use optional chaining and nullish coalescing for safer parsing
        const urlParts = agencyConfig.clientBaseUrl?.split('://') || [];
        const domainParts = urlParts[1]?.split(':') || [];
        const clientBaseDomain = domainParts[0];

        if (clientBaseDomain && clientBaseDomain !== window.location.hostname) {
          window.sessionStorage.setItem(
            'oauth-whitelabel-domain',
            clientBaseDomain,
          );
        }

        const redirectUrl = `${agencyConfig.clientBaseUrl
          }/oauth/whitelabel/callback?token=${token}&action=${action || ''
          }&trackingId=${trackingId || ''} &firstName=${firstName || ''
          } &lastName=${lastName || ''} &email=${email || ''}`;
        window.location.href = redirectUrl;
        return;
      }

      const isUtmSourceEmailVerifier = utmSource === 'email-verifier';
      const isUtmSourceEmailSpamChecker = utmSource === 'email-spam-checker';
      const isUtmSourceGrowthHub = utmSource === 'growth-hub';
      let redirectUrl = '';

      // Otherwise handle normal authentication flow
      switch (action) {
        case ActionForAuthentication.Invitation:
        case ActionForAuthentication.Signup:
          setToken(token);

          // Track signup with Tolt referral system (only for Signup, not Invitation)
          // Uses polling to wait for Tolt script (loaded via GTM) to be available
          if (action === ActionForAuthentication.Signup) {
            const { method } = match.params;
            if (method === OAuthMethod.Google) {
              pushSignupEvent('google');
            } else if (method === OAuthMethod.Microsoft) {
              pushSignupEvent('microsoft');
            }

            let attempts = 0;
            const toltInterval = setInterval(() => {
              attempts++;
              if (window.tolt_referral && window.tolt) {
                window.tolt.signup(email);
                clearInterval(toltInterval);
              } else if (attempts >= 10) {
                clearInterval(toltInterval);
              }
            }, 1000);
          }

          if (isUtmSourceEmailVerifier) {
            redirectWithoutRefresh({
              pathname: '/email-verifier',
              search: 'page=1&limit=25',
            });
            break;
          }

          if (isUtmSourceEmailSpamChecker) {
            redirectWithoutRefresh({
              pathname: '/inbox-radar',
            });
            break;
          }

          if (isUtmSourceGrowthHub) {
            redirectWithoutRefresh({
              pathname: '/growth-hub',
            });
            break;
          }

          // ** Signup: Redirect to lead finder page **
          if (redirectLeadFinderUrl) {
            const [pathname, search] = generateSerpLeadRedirectUrl(
              redirectLeadFinderUrl,
            )?.split('?');
            redirectWithoutRefresh({
              pathname,
              search,
            });
            break;
          }

          redirectWithoutRefresh({
            pathname: '/sequence',
            search: 'signup=completed',
          });
          break;
        case ActionForAuthentication.ValidationChangeEmail:
          setToken(token);

          redirectWithoutRefresh({
            pathname: '/settings/profile',
            state: {
              oAuthChangeEmail: true,
            },
          });
          break;
        case ActionForAuthentication.DeleteAccount:
          sendDeleteMyProfileRequest('null');
          break;
        case ActionForAuthentication.Login:
        default: {
          // MCP OAuth: show success page then redirect to authorize endpoint
          const mcpReturnUrl = sessionStorage.getItem('mcp_return_url');
          if (mcpReturnUrl && token) {
            sessionStorage.removeItem('mcp_return_url');
            AuthHelper.login({ token, redirect: false });
            sessionStorage.setItem('mcp_token', token);
            redirectWithoutRefresh(`/mcp-auth-success?returnUrl=${encodeURIComponent(mcpReturnUrl)}`);
            return;
          }

          if (redirectLeadFinderUrl) {
            redirectUrl = generateSerpLeadRedirectUrl(
              redirectLeadFinderUrl || '',
            );
          }
          if (isUtmSourceEmailVerifier) {
            redirectUrl = '/email-verifier?page=1&limit=25';
          }

          if (isUtmSourceEmailSpamChecker) {
            redirectUrl = '/inbox-radar';
          }

          if (isUtmSourceGrowthHub) {
            redirectUrl = '/growth-hub';
          }

          AuthHelper.login({
            token,
            ...(redirectUrl ? { redirectUrl } : {}),
          });
          initializeThirdPartyIntegrations({
            trackingId,
            showChatSupport: agencyConfig?.showChatSupport,
            firstName,
            lastName,
            email,
          });
          if (!redirectLeadFinderUrl && !utmSource) {
            redirectToHome(); // Redirect to home on success
          }
          toaster.success(message, { theme: Theme.New });
        }
      }
    }
  }, [getAgencyConfigRequestStatus, agencyConfig]);

  useEffect(() => {
    executeOnRequestStatus({
      status: deleteMyProfileRequestStatus,
      onSuccess: () => {
        toaster.success(deleteMyProfileRequestMessage, { theme: Theme.New });
        AuthHelper.redirectOnAccountDeletion('/signup');
        resetDeleteMyProfileRequest();
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: deleteMyProfileRequestError,
          onError: () => {
            if (deleteMyProfileRequestError.code !== 1001) {
              toaster.error(deleteMyProfileRequestError.message, {
                theme: Theme.New,
              });
              resetDeleteMyProfileRequest();
            }
          },
        });
      },
    });
  }, [deleteMyProfileRequestStatus]);

  return null;
};

export default OAuthAuthenticateCallback;
